import { useApolloClient } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import { includes } from 'lodash-es';

import { EmployerPurchaseProductType } from '../../../../../common/interfaces/entities';
import useUpdateFPACache from '../../../hooks/useUpdateFPACache';
import { CreditProductPurchaseOrderStatus } from '../../../types';
import { featureCountRelatedProducts } from '../constants';

export default function useRefetchAfterCreditPurchase() {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const updateFPACache = useUpdateFPACache();

  const refetchAfterCreditPurchase = ({
    productType,
    jobId,
    status,
  }: {
    productType: EmployerPurchaseProductType;
    jobId?: string;
    status?: CreditProductPurchaseOrderStatus;
  }) => {
    const vipQueriesToRefetch =
      productType === EmployerPurchaseProductType.VIP_MEMBERSHIP
        ? ['getVIPMembershipInfo']
        : [];

    const talentSearchQueriesToRefetch =
      productType === EmployerPurchaseProductType.TALENT_SEARCH
        ? ['getTalentSearchInventory']
        : [];

    const hotJobQueriesToRefetch =
      productType === EmployerPurchaseProductType.HOT_JOB
        ? [
            'getFullProfileAccessBalance',
            'getCompanyCreditInfo',
            ...(jobId ? ['getJobById'] : []),
          ]
        : [];

    const premiumPlanQueriesToRefetch =
      productType === EmployerPurchaseProductType.PREMIUM_PLAN
        ? ['getCurrentCompanyJobSlotsSubscription', 'getVIPMembershipInfo']
        : [];

    const contactPacksQueryToRefetch =
      productType === EmployerPurchaseProductType.SHORTLIST
        ? ['getContactPacksInfo']
        : [];

    const recommendedTalentQueriesToRefetch =
      productType === EmployerPurchaseProductType.RECOMMENDED_TALENT_CHAT
        ? ['getRecommendedTalentMonetaryValue']
        : [];

    const queriesToRefetch = [
      'getPurchasedProducts',
      'getCompanyCreditInfo',
      'getProductUsageRecords',
      ...vipQueriesToRefetch,
      ...talentSearchQueriesToRefetch,
      ...hotJobQueriesToRefetch,
      ...premiumPlanQueriesToRefetch,
      ...contactPacksQueryToRefetch,
      ...recommendedTalentQueriesToRefetch,
    ];

    void apolloClient.refetchQueries({
      include: queriesToRefetch,
    });

    void queryClient.invalidateQueries({ queryKey: ['productPurchases'] });

    if (
      productType === EmployerPurchaseProductType.HOT_JOB &&
      jobId &&
      status === CreditProductPurchaseOrderStatus.SUCCESS
    ) {
      void queryClient.invalidateQueries({ queryKey: ['job-by-id', jobId] });
    }

    void queryClient.removeQueries({ queryKey: ['purchasePreview'] });

    if (
      includes(featureCountRelatedProducts, productType) &&
      status === CreditProductPurchaseOrderStatus.SUCCESS
    ) {
      updateFPACache({ jobId });
    }
  };

  return refetchAfterCreditPurchase;
}
